import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';

import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useMutation } from '@sorare/core/src/hooks/graphql/useMutation';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useCloseDialog } from '@sorare/core/src/hooks/navigation/useCloseDialog';
import { useDialogParam } from '@sorare/core/src/hooks/navigation/useDialogParam';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { ensureHexColor } from '@sorare/core/src/lib/colors';
import { isType } from '@sorare/core/src/lib/gql';

import { RevealPackCardsDialog } from 'components/commonCard/RevealPackCardsDialog';

import { BuyCardPackFlow } from './BuyCardFlow';
import type {
  BuyCardPackContent_cardPack,
  BuyCardPackDialogQuery,
  BuyCardPackDialogQueryVariables,
  BuyCardPackMutation,
  BuyCardPackMutationVariables,
} from './__generated__/index.graphql';

const BUY_CARD_PACK_MUTATION = gql`
  mutation BuyCardPackMutation($input: buyCardPackInput!, $sport: Sport!) {
    buyCardPack(input: $input) {
      currentUser {
        slug
        gemsBalances: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [COMMON_GEM]
        ) {
          amount
        }
      }
      cards {
        slug
        ...RevealPackCardsDialog_anyCardInterface
      }
      pack {
        id
        cardPack {
          slug
          contributesToUnlockTask {
            id
            ... on StarPackTask {
              id
              aasmState
              progress
            }
          }
        }
      }
      errors {
        code
        message
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<BuyCardPackMutation, BuyCardPackMutationVariables>;

type Props = {
  cardPack: BuyCardPackContent_cardPack;
};
export const BuyCardPackContent = ({ cardPack }: Props) => {
  const {
    flags: { disableNativeIOSPackOpening = false },
  } = useFeatureFlags();
  const { isIosApp, postMessage } = useIsMobileApp();
  const rewardConfig = cardPack.contributesToUnlockTask?.rewardConfigs?.[0];
  const starCardPackSlug = isType(rewardConfig, 'CardPacksRewardConfig')
    ? rewardConfig.cardPack?.slug
    : undefined;
  const { pictureUrl } = cardPack;
  const { sport } = useSportContext();
  const [buyCardPackData, setBuyCardPackData] =
    useState<BuyCardPackMutation['buyCardPack']>();
  const getInteractionContext = useGetInteractionContext();

  const useNativeIOSPackOpening = isIosApp && !disableNativeIOSPackOpening;

  const [buyCardPack] = useMutation(BUY_CARD_PACK_MUTATION, {
    showErrorsWithSnackNotification: true,
    update: (cache, { data: mutationData }) => {
      const contributesToUnlockTask =
        mutationData?.buyCardPack?.pack?.cardPack?.contributesToUnlockTask;
      if (!contributesToUnlockTask || !starCardPackSlug) {
        return;
      }
      // update star pack rewardedByTask field if it exists
      // this is needed because initially rewardedByTask is a template
      // and the id will change when it becomes a real task
      cache.modify({
        id: cache.identify({
          slug: starCardPackSlug,
          __typename: 'CardPack',
        }),
        fields: {
          rewardedByTask: () =>
            mutationData?.buyCardPack?.pack?.cardPack
              ?.contributesToUnlockTask || undefined,
        },
      });
    },
  });
  const closeDialog = useCloseDialog();
  const handleClose = () => closeDialog(DialogKey.cardPack);

  useEffect(() => {
    if (useNativeIOSPackOpening) {
      postMessage('buyCardPack', {
        packId: cardPack.slug,
      });
    }
  }, [cardPack.slug, postMessage, useNativeIOSPackOpening]);

  if (useNativeIOSPackOpening) {
    return null;
  }

  const onPurchase = async () => {
    const { data } = await buyCardPack({
      variables: {
        input: {
          cardPackSlug: cardPack.slug,
          sport,
          interactionContext: getInteractionContext(),
        },
        sport,
      },
    });
    setBuyCardPackData(data?.buyCardPack);
  };
  const packNotBought =
    buyCardPackData?.cards?.length === 0 || !buyCardPackData?.pack?.id;

  return (
    <>
      {packNotBought ? (
        <BuyCardPackFlow
          onClose={handleClose}
          cardPack={cardPack}
          onPurchase={onPurchase}
        />
      ) : (
        <RevealPackCardsDialog
          open
          onClose={handleClose}
          cards={buyCardPackData!.cards!}
          onContinue={handleClose}
          cardSetSlug={cardPack.cardSet?.slug || ''}
          packId={buyCardPackData!.pack!.id!}
          packPictureUrl={pictureUrl}
          primaryColor={ensureHexColor(cardPack.primaryColor)}
          secondaryColor={ensureHexColor(cardPack.secondaryColor)}
          showPlayerDetails
        />
      )}
    </>
  );
};

BuyCardPackContent.fragments = {
  cardPack: gql`
    fragment BuyCardPackContent_cardPack on CardPack {
      slug
      pictureUrl
      cardSet {
        slug
      }
      contributesToUnlockTask {
        id
        rewardConfigs {
          id
          ... on CardPacksRewardConfig {
            id
            cardPack {
              slug
            }
          }
        }
      }
      ...BuyCardPackFlow_cardPack
    }
    ${BuyCardPackFlow.fragments.cardPack}
  ` as TypedDocumentNode<BuyCardPackContent_cardPack>,
};

const QUERY = gql`
  query BuyCardPackDialogQuery($cardPackSlug: String!) {
    market {
      id
      cardPack(slug: $cardPackSlug) {
        slug
        ...BuyCardPackContent_cardPack
      }
    }
  }
  ${BuyCardPackContent.fragments.cardPack}
` as TypedDocumentNode<BuyCardPackDialogQuery, BuyCardPackDialogQueryVariables>;

export const BuyCardPackDialog = () => {
  const cardPackSlug = useDialogParam(DialogKey.cardPack);
  const { data } = useQuery(QUERY, {
    variables: { cardPackSlug: cardPackSlug! },
    skip: !cardPackSlug,
  });
  const cardPack = data?.market.cardPack;
  if (!cardPack) {
    return null;
  }
  return <BuyCardPackContent cardPack={cardPack} />;
};
